import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { StatCard } from "../components/dashboard/StatCard";
import { DeskOverview } from "../components/dashboard/DeskOverview";
import { LeadImport } from "../components/leads/LeadImport";
import { useAuth } from "../hooks/useAuth";
import {
    Users,
    ArrowUpDown,
    UserCheck,
    Headphones,
    Upload,
    Filter,
    Calendar,
    X,
    Search,
    FileText,
    Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { format } from "date-fns";
import { fetchLeads } from "../services/crmServices/leadService.js";
import { fetchDesks } from "../services/crmServices/deskService.js";
import { fetchUsers } from "../services/crmServices/userService.js";
import { getDashboardStats, searchDashboard } from "../services/crmServices/dashboardService.js";
import { fetchTransfers } from "../services/crmServices/transferService.js";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { toCamelCase, getFullName } from "../services/common/util.js";
import moment from "moment";

export function Dashboard() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const [timeRange, setTimeRange] = useState("month");
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [customDateRange, setCustomDateRange] = useState({
        startDate: null,
        endDate: null
    });
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [stats, setStats] = useState({
        totalLeads: 0,
        totalTransfers: 0,
        totalActiveClients: 0,
        totalAgents: 0
    });
    const [leads, setLeads] = useState([]);
    const [desks, setDesks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false); // Separate state for refreshing data
    const [allData, setAllData] = useState({});

    // Transfer history state
    const [allTransfers, setAllTransfers] = useState([]);
    const [filteredTransfers, setFilteredTransfers] = useState([]);
    const [transfersLoading, setTransfersLoading] = useState(false);
    const [transferFilters, setTransferFilters] = useState({
        searchTerm: "",
        status: "",
        agent: "",
        desk: "",
        dateFrom: null,
        dateTo: null
    });
    const [showTransferHistory, setShowTransferHistory] = useState(true);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [availableDesks, setAvailableDesks] = useState([]);

    // Memoize the date range calculation to avoid unnecessary recalculations
    const calculateDateRange = useCallback((range) => {
        const now = new Date();
        let startDate = null, endDate = null;
        
        switch (range) {
            case "1hour": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 1 * 60 * 60 * 1000);
                break;
            }
            case "6hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
                break;
            }
            case "12hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
                break;
            }
            case "24hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
            }
            case "today": {
                startDate = new Date();
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date();
                endDate.setHours(24, 0, 0, 0);
                break;
            }
            case "week": {
                startDate = new Date(now);
                const day = now.getDay();
                const diffToMonday = day === 0 ? -6 : 1 - day;
                startDate.setDate(now.getDate() + diffToMonday);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 7);
                break;
            }
            case "month": {
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                break;
            }
            default:
                return null;
        }
        
        return { startDate, endDate };
    }, []);

    // Update date range when timeRange changes, but batch the state update
    useEffect(() => {
        if (timeRange !== "custom") {
            const dateRange = calculateDateRange(timeRange);
            if (dateRange) {
                // Batch state updates to prevent multiple re-renders
                setCustomDateRange(dateRange);
                setShowCustomDatePicker(false);
            }
        } else {
            setShowCustomDatePicker(true);
        }
    }, [timeRange, calculateDateRange]);

    // Memoize initPage to prevent recreation on every render
    const initPage = useCallback(async (isInitialLoad = false) => {
        const { startDate, endDate } = customDateRange;
        if (!startDate || !endDate) return;
        
        // Use loading for initial load, refreshing for subsequent updates
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }
        
        try {
            const formattedStartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
            const formattedEndDate = endDate instanceof Date ? endDate.toISOString() : endDate;
            
            const res = await searchDashboard({ 
                timeRange: timeRange, 
                startDate: formattedStartDate, 
                endDate: formattedEndDate 
            });
            
            if (res.data) {
                setAllData(res.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [customDateRange, timeRange]);

    // Initial load - wait for date range to be set first
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (!hasInitialized.current && customDateRange.startDate && customDateRange.endDate) {
            hasInitialized.current = true;
            initPage(true); // Initial load
        }
    }, [customDateRange, initPage]);

    // Only fetch data when customDateRange actually changes (not on every render)
    const prevDateRangeRef = useRef(customDateRange);
    useEffect(() => {
        // Skip initial mount
        if (!prevDateRangeRef.current.startDate) {
            prevDateRangeRef.current = customDateRange;
            return;
        }
        
        // Only fetch if the date range actually changed
        const prevRange = prevDateRangeRef.current;
        const rangeChanged = 
            prevRange.startDate?.getTime() !== customDateRange.startDate?.getTime() ||
            prevRange.endDate?.getTime() !== customDateRange.endDate?.getTime();
        
        if (rangeChanged && customDateRange.startDate && customDateRange.endDate) {
            prevDateRangeRef.current = customDateRange;
            initPage(false); // Refresh, not initial load
        }
    }, [customDateRange, initPage]);

    const handleTimeRangeChange = useCallback((value) => {
        setTimeRange(value);
    }, []);

    const handleCustomDateChange = useCallback((field, date) => {
        setCustomDateRange((prev) => ({
            ...prev,
            [field]: date
        }));
    }, []);

    const clearCustomDateRange = () => {
        setCustomDateRange({ startDate: null, endDate: null });
        setTimeRange("month");
        setShowCustomDatePicker(false);
    };

    // Transfer history functions
    const fetchTransferHistory = async () => {
        setTransfersLoading(true);
        try {
            const [transfersResponse, usersResponse, desksResponse] = await Promise.all([
                fetchTransfers(),
                fetchUsers(),
                fetchDesks()
            ]);

            if (transfersResponse?.data) {
                let transfers = transfersResponse.data;
                // Filter based on user role
                if (user?.position?.role === "deskManager") {
                    transfers = transfers.filter((transfer) => transfer.resolvedBy?._id == user._id);
                }

                setAllTransfers(transfers);
                setFilteredTransfers(transfers);
            }

            if (usersResponse?.data) {
                setAvailableAgents(usersResponse.data.filter((u) => u.position?.role === "agent"));
            }

            if (desksResponse?.data) {
                setAvailableDesks(desksResponse.data);
            }
        } catch (error) {
            console.error("Error fetching transfer history:", error);
        } finally {
            setTransfersLoading(false);
        }
    };

    const applyTransferFilters = () => {
        let filtered = [...allTransfers];

        // Search term filter
        if (transferFilters.searchTerm.trim()) {
            filtered = filtered.filter(
                (transfer) =>
                    transfer.lead?.firstName?.toLowerCase().includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.lead?.lastName?.toLowerCase().includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.lead?.email?.toLowerCase().includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.requestedBy?.firstName
                        ?.toLowerCase()
                        .includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.requestedBy?.lastName
                        ?.toLowerCase()
                        .includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.targetAgent?.firstName
                        ?.toLowerCase()
                        .includes(transferFilters.searchTerm.toLowerCase()) ||
                    transfer.targetAgent?.lastName?.toLowerCase().includes(transferFilters.searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (transferFilters.status) {
            filtered = filtered.filter((transfer) => transfer.status === transferFilters.status);
        }

        // Agent filter
        if (transferFilters.agent) {
            filtered = filtered.filter(
                (transfer) =>
                    (transfer.requestedBy?._id || transfer.requestedBy) === transferFilters.agent ||
                    (transfer.targetAgent?._id || transfer.targetAgent) === transferFilters.agent
            );
        }

        // Desk filter
        if (transferFilters.desk) {
            filtered = filtered.filter((transfer) => transfer.lead?.desk?._id === transferFilters.desk);
        }

        // Date filters
        if (transferFilters.dateFrom) {
            filtered = filtered.filter((transfer) => new Date(transfer.createdAt) >= transferFilters.dateFrom);
        }

        if (transferFilters.dateTo) {
            const endDate = new Date(transferFilters.dateTo);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter((transfer) => new Date(transfer.createdAt) <= endDate);
        }

        setFilteredTransfers(filtered);
    };

    const handleTransferFilterChange = (field, value) => {
        setTransferFilters((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const clearTransferFilters = () => {
        setTransferFilters({
            searchTerm: "",
            status: "",
            agent: "",
            desk: "",
            dateFrom: null,
            dateTo: null
        });
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case "approved":
                return "bg-green-100 text-green-800";
            case "rejected":
                return "bg-red-100 text-red-800";
            case "pending":
                return "bg-yellow-100 text-yellow-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    // Effect to apply filters whenever they change
    useEffect(() => {
        if (allTransfers.length > 0) {
            applyTransferFilters();
        }
    }, [transferFilters, allTransfers]);

    // Fetch transfer history when component mounts or when showing transfer history
    useEffect(() => {
        if (showTransferHistory && (user?.position?.role === "deskManager" || user?.position?.role === "admin")) {
            fetchTransferHistory();
        }
    }, [showTransferHistory, user?.position?.role]);

    // Only show full loading on initial load
    if (loading && Object.keys(allData).length === 0) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </div>
        );
    }

    // Show different dashboard based on role
    // Keep content visible during refresh, just show a subtle indicator
    if (Object.keys(allData).length > 0 || !loading) {
        if (user?.position.role === "agent") {
            return (
                <div className="container mx-auto px-6">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Agent Dashboard</h1>
                        <div className="flex items-center space-x-3">
                            <Filter className="h-4 w-4 text-gray-500" />
                            <Select
                                options={[
                                    { label: "Last 1 Hour", value: "1hour" },
                                    { label: "Last 6 Hours", value: "6hours" },
                                    { label: "Last 12 Hours", value: "12hours" },
                                    { label: "Last 24 Hours", value: "24hours" },
                                    { label: "Today", value: "today" },
                                    { label: "This Week", value: "week" },
                                    { label: "This Month", value: "month" },
                                    { label: "Custom", value: "custom" }
                                ]}
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="w-40"
                            />
                            {timeRange === "custom" && (
                                <div className="flex items-center space-x-2">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.startDate
                                                    ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                    : "Start Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.startDate}
                                                onSelect={(date) => handleCustomDateChange("startDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.endDate
                                                    ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                    : "End Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.endDate}
                                                onSelect={(date) => handleCustomDateChange("endDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearCustomDateRange}
                                        title="Clear custom date range"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subtle refreshing indicator */}
                    {refreshing && (
                        <div className="fixed top-20 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span className="text-sm font-medium">Updating data...</span>
                        </div>
                    )}

                    <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8 ${refreshing ? 'opacity-60' : ''}`}>
                        <StatCard 
                            title="My Leads" 
                            value={allData.leadsCount ?? 0} 
                            icon={Users} 
                            color="bg-blue-500"
                        />
                        <StatCard
                            title="Active Clients"
                            value={allData.activeClientsCount ?? 0}
                            icon={UserCheck}
                            color="bg-green-500"
                        />
                        <StatCard
                            title="Transfers"
                            value={allData.totalTransfersCount ?? 0}
                            icon={ArrowUpDown}
                            color="bg-purple-500"
                        />
                        {/* <StatCard title="Tasks" value={allData.tasksCount} icon={Headphones} color="bg-orange-500" /> */}
                    </div>

                    {/* Agent-specific lead breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="text-lg font-semibold mb-4">My Lead Status Breakdown</h3>
                            <div className="space-y-3">
                                {leadStatuses && leadStatuses.length > 0 ? (
                                    leadStatuses
                                        .map((statusOption) => {
                                            const status = statusOption.value;
                                            // Get count from breakdown (0 if status not found in agent's leads)
                                            const count = allData.leadStatusBreakdown?.[status] || 0;
                                            return { statusOption, count };
                                        })
                                        .filter(({ count }) => count > 0) // Only show statuses with count > 0
                                        .map(({ statusOption, count }) => (
                                            <div key={statusOption.value} className="flex justify-between items-center">
                                                <span className="text-sm">{statusOption.label}</span>
                                                <span
                                                    className="px-2 py-1 rounded text-sm font-medium"
                                                    style={{
                                                        backgroundColor: statusOption.bgColor || "#f3f4f6",
                                                        color: statusOption.color || "#1f2937"
                                                    }}
                                                >
                                                    {count}
                                                </span>
                                            </div>
                                        ))
                                ) : (
                                    // Fallback: Show statuses from breakdown data if Redux statuses are not loaded
                                    allData.leadStatusBreakdown ? (
                                        Object.entries(allData.leadStatusBreakdown)
                                            .filter(([status, count]) => count > 0) // Only show statuses with count > 0
                                            .map(([status, count]) => (
                                                <div key={status} className="flex justify-between items-center">
                                                    <span className="text-sm capitalize">{status.replace(/_/g, " ")}</span>
                                                    <span className="px-2 py-1 rounded text-sm font-medium bg-gray-100 text-gray-800">
                                                        {count}
                                                    </span>
                                                </div>
                                            ))
                                    ) : (
                                        <div className="text-sm text-gray-500">No leads found</div>
                                    )
                                )}
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow">
                            <h3 className="text-lg font-semibold mb-4">Performance Metrics</h3>
                            <div className="space-y-4">
                                {/* VoIP Call Statistics */}
                                <div className="border-b pb-3">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">VoIP Calls</h4>
                                    <div className="space-y-2">
                                        {allData.voipCallStats ? (
                                            <>
                                                {/* Show all VoIP providers that user has access to */}
                                                {user.permissions?.voip?.commpeak && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">CommPeak</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.commpeak > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.commpeak || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.voiso && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">Voiso</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.voiso > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.voiso || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.coperato && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">Coperato</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.coperato > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.coperato || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.squaretalk && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">SquareTalk</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.squaretalk > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.squaretalk || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.globalcall && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">GlobalCall</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.globalcall > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.globalcall || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.bubblevoip && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-600">Bubble VoIP</span>
                                                        <span
                                                            className={`font-semibold ${
                                                                allData.voipCallStats.bubblevoip > 0
                                                                    ? "text-gray-900"
                                                                    : "text-gray-400"
                                                            }`}
                                                        >
                                                            {allData.voipCallStats.bubblevoip || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center pt-2 border-t mt-2">
                                                    <span className="text-sm font-medium text-gray-700">Total Calls</span>
                                                    <span className="font-bold text-blue-600">
                                                        {allData.voipCallStats.total || 0}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-sm text-gray-500">No call data available</div>
                                        )}
                                    </div>
                                </div>

                                {/* Conversion and Response Rates */}
                                <div className="pt-3">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">Conversion Metrics</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Conversion Rate</span>
                                            <span className="font-semibold text-gray-900">
                                                {allData.metrics?.conversionRate?.toFixed(2) || 0}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Response Rate</span>
                                            <span className="font-semibold text-gray-900">
                                                {allData.metrics?.responseRate?.toFixed(2) || 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (user?.position?.role === "deskManager") {
            return (
                <div className="container mx-auto px-6">
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Desk Dashboard</h1>
                        <div className="flex items-center space-x-3">
                            <Filter className="h-4 w-4 text-gray-500" />
                            <Select
                                options={[
                                    { label: "Last 1 Hour", value: "1hour" },
                                    { label: "Last 6 Hours", value: "6hours" },
                                    { label: "Last 12 Hours", value: "12hours" },
                                    { label: "Last 24 Hours", value: "24hours" },
                                    { label: "Today", value: "today" },
                                    { label: "This Week", value: "week" },
                                    { label: "This Month", value: "month" },
                                    { label: "Custom", value: "custom" }
                                ]}
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="w-40"
                            />
                            {timeRange === "custom" && (
                                <div className="flex items-center space-x-2">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.startDate
                                                    ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                    : "Start Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.startDate}
                                                onSelect={(date) => handleCustomDateChange("startDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.endDate
                                                    ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                    : "End Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.endDate}
                                                onSelect={(date) => handleCustomDateChange("endDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearCustomDateRange}
                                        title="Clear custom date range"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <StatCard
                            title="Team Agents"
                            value={allData.totalAgentsCount || 0}
                            icon={Users}
                            color="bg-blue-500"
                        />
                        <StatCard title="Team Leads" value={allData.leadsCount || 0} icon={Users} color="bg-green-500" />
                        <StatCard
                            title="Active Clients"
                            value={allData.activeClientsCount || 0}
                            icon={UserCheck}
                            color="bg-purple-500"
                        />
                        <StatCard
                            title="Transfers"
                            value={allData.totalTransfersCount || 0}
                            icon={ArrowUpDown}
                            color="bg-orange-500"
                        />
                    </div>

                    <div className={refreshing ? 'opacity-60' : ''}>
                        <DeskOverview desks={allData.desks || []} timeRange={timeRange} />
                    </div>
                </div>
            );
        }

        // Admin Dashboard (default)
        return (
            <div className="container mx-auto px-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">
                        {user?.position?.role === "admin"
                            ? "Admin"
                            : user.position.floor?.charAt(0).toUpperCase() + user.position.floor?.slice(1)}{" "}
                        Dashboard
                    </h1>
                    <div className="flex items-center space-x-3">
                        <Filter className="h-4 w-4 text-gray-500" />
                        <Select
                            options={[
                                { label: "Last 1 Hour", value: "1hour" },
                                { label: "Last 6 Hours", value: "6hours" },
                                { label: "Last 12 Hours", value: "12hours" },
                                { label: "Last 24 Hours", value: "24hours" },
                                { label: "Today", value: "today" },
                                { label: "This Week", value: "week" },
                                { label: "This Month", value: "month" },
                                { label: "Custom", value: "custom" }
                            ]}
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            className="w-40"
                        />
                        {timeRange === "custom" && (
                            <div className="flex items-center space-x-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-[140px] justify-start text-left font-normal"
                                        >
                                            <Calendar className="mr-2 h-4 w-4" />
                                            {customDateRange.startDate
                                                ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                : "Start Date"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarComponent
                                            mode="single"
                                            selected={customDateRange.startDate}
                                            onSelect={(date) => handleCustomDateChange("startDate", date)}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-[140px] justify-start text-left font-normal"
                                        >
                                            <Calendar className="mr-2 h-4 w-4" />
                                            {customDateRange.endDate
                                                ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                : "End Date"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarComponent
                                            mode="single"
                                            selected={customDateRange.endDate}
                                            onSelect={(date) => handleCustomDateChange("endDate", date)}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={clearCustomDateRange}
                                    title="Clear custom date range"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard title="Total Leads" value={allData.leadsCount || 0} icon={Users} color="bg-blue-500" />
                    <StatCard
                        title="Total Transfers"
                        value={allData.totalTransfersCount || 0}
                        icon={ArrowUpDown}
                        color="bg-green-500"
                    />
                    <StatCard
                        title="Active Clients"
                        value={allData.activeClientsCount || 0}
                        icon={UserCheck}
                        color="bg-purple-500"
                    />
                    <StatCard
                        title="Total Agents"
                        value={allData.totalAgentsCount || 0}
                        icon={Headphones}
                        color="bg-orange-500"
                    />
                </div>

                <DeskOverview desks={allData.desks || []} timeRange={timeRange} />

                {showImport && <LeadImport onClose={() => setShowImport(false)} />}
            </div>
        );
    }
}
